import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { ProjectCreditsSummary, LLMModelInfo } from '../types';
import { getProjectCreditsSummary, getProject, listLLMProvidersPublic, listActiveVideoModels, listActiveImageModels } from '../api';
import Breadcrumb from '../components/Breadcrumb';
import EmptyState from '../components/EmptyState';
import { creditsToYuan } from '../utils/currency';
import { useTranslation } from 'react-i18next';
import { toI18nKeySegment } from '../utils/i18nKey';

// OPERATION_LABELS moved to i18n: profile.operationTypes.*

// TYPE_LABELS moved to i18n: profile.categoryTypes.*

function formatNumber(n: number): string {
  return n.toLocaleString('zh-CN');
}

function formatTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toString();
}

const ProjectCredits: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation('profile');
  const [projectName, setProjectName] = useState('');
  const [data, setData] = useState<ProjectCreditsSummary[]>([]);
  const [modelNames, setModelNames] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    const nameMap: Record<string, string> = {};
    Promise.all([
      getProject(id).then(p => setProjectName(p.name)).catch(() => {}),
      getProjectCreditsSummary(id).then(d => setData(d || [])).catch(() => {}),
      listLLMProvidersPublic().then(providers => {
        for (const p of providers) {
          try {
            const models: LLMModelInfo[] = JSON.parse(p.models || '[]');
            for (const m of models) { if (m.displayName) nameMap[m.name] = m.displayName; }
          } catch {}
        }
      }).catch(() => {}),
      listActiveVideoModels().then(models => {
        for (const m of models) { if (m.displayName) nameMap[m.model] = m.displayName; }
      }).catch(() => {}),
      listActiveImageModels().then(models => {
        for (const m of models) { if (m.displayName) nameMap[m.model] = m.displayName; }
      }).catch(() => {}),
    ]).then(() => setModelNames(nameMap)).finally(() => setLoading(false));
  }, [id]);

  // Group by operation type
  const llmData = data.filter(d => d.operationType === 'llm');
  const imageData = data.filter(d => d.operationType === 'image');
  const videoData = data.filter(d => d.operationType === 'video');
  const serviceData = data.filter(d => d.operationType === 'service');
  const searchData = data.filter(d => d.operationType === 'search');

  const llmTotal = llmData.reduce((a, r) => ({ credits: a.credits + r.totalCredits, calls: a.calls + r.callCount, input: a.input + r.inputTokens, output: a.output + r.outputTokens }), { credits: 0, calls: 0, input: 0, output: 0 });
  const imageTotal = imageData.reduce((a, r) => ({ credits: a.credits + r.totalCredits, calls: a.calls + r.callCount }), { credits: 0, calls: 0 });
  const videoTotal = videoData.reduce((a, r) => ({ credits: a.credits + r.totalCredits, calls: a.calls + r.callCount }), { credits: 0, calls: 0 });
  const serviceTotal = serviceData.reduce((a, r) => ({ credits: a.credits + r.totalCredits, calls: a.calls + r.callCount }), { credits: 0, calls: 0 });
  const searchTotal = searchData.reduce((a, r) => ({ credits: a.credits + r.totalCredits, calls: a.calls + r.callCount }), { credits: 0, calls: 0 });
  const grandTotal = llmTotal.credits + imageTotal.credits + videoTotal.credits + serviceTotal.credits + searchTotal.credits;

  if (loading) {
    return (
      <div className="container">
        <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-3)' }}>{t('projectCredits.loading')}</div>
      </div>
    );
  }

  return (
    <div className="container">
      <Breadcrumb items={[
        { label: t('projectCredits.breadcrumbProjects'), to: '/projects' },
        { label: projectName || t('projectCredits.breadcrumbProject'), to: `/projects/${id}` },
        { label: t('projectCredits.breadcrumbTitle') },
      ]} />

      <div className="page-header">
        <h1 className="page-title">{t('projectCredits.pageTitle')}</h1>
      </div>

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.75rem', marginBottom: '1.5rem' }}>
        <div className="card" style={{ textAlign: 'center', padding: '1.25rem' }}>
          <div style={{ fontSize: '0.8rem', color: 'var(--text-3)', marginBottom: '0.35rem' }}>{t('projectCredits.llmCost')}</div>
          <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text)' }}>¥{creditsToYuan(llmTotal.credits)}</div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-3)' }}>{t('projectCredits.llmCallsTokens', { calls: formatNumber(llmTotal.calls), tokens: formatTokens(llmTotal.input + llmTotal.output) })}</div>
        </div>
        <div className="card" style={{ textAlign: 'center', padding: '1.25rem' }}>
          <div style={{ fontSize: '0.8rem', color: 'var(--text-3)', marginBottom: '0.35rem' }}>{t('projectCredits.imageCost')}</div>
          <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text)' }}>¥{creditsToYuan(imageTotal.credits)}</div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-3)' }}>{t('projectCredits.imageCallsLabel', { calls: formatNumber(imageTotal.calls) })}</div>
        </div>
        <div className="card" style={{ textAlign: 'center', padding: '1.25rem' }}>
          <div style={{ fontSize: '0.8rem', color: 'var(--text-3)', marginBottom: '0.35rem' }}>{t('projectCredits.videoCost')}</div>
          <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text)' }}>¥{creditsToYuan(videoTotal.credits)}</div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-3)' }}>{t('projectCredits.videoCallsLabel', { calls: formatNumber(videoTotal.calls) })}</div>
        </div>
        {serviceTotal.credits > 0 && (
        <div className="card" style={{ textAlign: 'center', padding: '1.25rem' }}>
          <div style={{ fontSize: '0.8rem', color: 'var(--text-3)', marginBottom: '0.35rem' }}>{t('projectCredits.serviceCost')}</div>
          <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text)' }}>¥{creditsToYuan(serviceTotal.credits)}</div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-3)' }}>{t('projectCredits.serviceCallsLabel', { calls: formatNumber(serviceTotal.calls) })}</div>
        </div>
        )}
        {searchTotal.credits > 0 && (
        <div className="card" style={{ textAlign: 'center', padding: '1.25rem' }}>
          <div style={{ fontSize: '0.8rem', color: 'var(--text-3)', marginBottom: '0.35rem' }}>{t('projectCredits.searchCost')}</div>
          <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text)' }}>¥{creditsToYuan(searchTotal.credits)}</div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-3)' }}>{t('projectCredits.searchCallsLabel', { calls: formatNumber(searchTotal.calls) })}</div>
        </div>
        )}
        <div className="card" style={{ textAlign: 'center', padding: '1.25rem', background: 'var(--bg-2, var(--surface-2, #f9fafb))' }}>
          <div style={{ fontSize: '0.8rem', color: 'var(--text-3)', marginBottom: '0.35rem' }}>{t('projectCredits.totalCost')}</div>
          <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--warning)' }}>¥{creditsToYuan(grandTotal)}</div>
        </div>
      </div>

      {/* Detail table */}
      <div className="card">
        <h3 style={{ margin: '0 0 1rem 0', fontSize: '1rem' }}>{t('projectCredits.detailTitle')}</h3>
        {data.length > 0 ? (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--border)' }}>
                  <th style={thStyle}>{t('projectCredits.thType')}</th>
                  <th style={thStyle}>{t('projectCredits.thOperation')}</th>
                  <th style={thStyle}>{t('projectCredits.thModel')}</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>{t('projectCredits.thCalls')}</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>{t('projectCredits.thInputToken')}</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>{t('projectCredits.thOutputToken')}</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>{t('projectCredits.thCost')}</th>
                </tr>
              </thead>
              <tbody>
                {data.map((row, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={tdStyle}>
                      <span className="status-badge status-uploading" style={{ fontSize: '0.75rem' }}>
                        {t('categoryTypes.' + toI18nKeySegment(row.operationType), { defaultValue: row.operationType })}
                      </span>
                    </td>
                    <td style={tdStyle}>{t('operationTypes.' + toI18nKeySegment(row.operationName), { defaultValue: row.operationName })}</td>
                    <td style={{ ...tdStyle, color: 'var(--text-2)', fontSize: '0.8rem' }}>{modelNames[row.modelName] || row.modelName || '-'}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>{formatNumber(row.callCount)}</td>
                    <td style={{ ...tdStyle, textAlign: 'right', color: 'var(--text-2)' }}>{(row.operationType === 'llm' || row.inputTokens > 0) ? formatTokens(row.inputTokens) : '-'}</td>
                    <td style={{ ...tdStyle, textAlign: 'right', color: 'var(--text-2)' }}>{(row.operationType === 'llm' || row.outputTokens > 0) ? formatTokens(row.outputTokens) : '-'}</td>
                    <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 500, color: 'var(--warning)' }}>¥{creditsToYuan(row.totalCredits)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ borderTop: '2px solid var(--border)' }}>
                  <td colSpan={3} style={{ ...tdStyle, fontWeight: 600 }}>{t('projectCredits.subtotal')}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>{formatNumber(data.reduce((a, r) => a + r.callCount, 0))}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600, color: 'var(--text-2)' }}>{formatTokens(data.reduce((a, r) => a + r.inputTokens, 0))}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600, color: 'var(--text-2)' }}>{formatTokens(data.reduce((a, r) => a + r.outputTokens, 0))}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600, color: 'var(--warning)' }}>¥{creditsToYuan(grandTotal)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        ) : (
          <EmptyState icon="&#128202;" description={t("projectCredits.emptyData")} />
        )}
      </div>
    </div>
  );
};

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '0.6rem 0.75rem',
  fontWeight: 600,
  color: 'var(--text-2)',
  whiteSpace: 'nowrap',
};

const tdStyle: React.CSSProperties = {
  padding: '0.6rem 0.75rem',
  whiteSpace: 'nowrap',
};

export default ProjectCredits;
